import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import { formatAuditType } from '../../../utils/tsc';
import './AdminAudits.css';

function fmt(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
}

function passRateColor(rate) {
  if (rate >= 80) return '#16a34a';
  if (rate >= 50) return '#ca8a04';
  return '#dc2626';
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  if (!status) return <span className="aa-badge aa-badge-norun">{t('AdminAudits.statusNotRun')}</span>;
  if (status === 'PASS') return <span className="aa-badge aa-badge-pass"><i className="fas fa-check" /> {t('AdminAudits.statusPass')}</span>;
  if (status === 'FAIL') return <span className="aa-badge aa-badge-fail"><i className="fas fa-times" /> {t('AdminAudits.statusFail')}</span>;
  return <span className="aa-badge">{status}</span>;
}

export default function AdminAudits() {
  const { t } = useTranslation();
  const [audits,       setAudits]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState('');
  const [fwFilter,     setFwFilter]     = useState('');
  const [page,         setPage]         = useState(0);
  const pageSize = 15;

  const [peek,         setPeek]         = useState(null);
  const [peekTests,    setPeekTests]    = useState([]);
  const [peekLoading,  setPeekLoading]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/audits/all', { params: { page: 0, size: 500 } });
      setAudits(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPeek = async (audit) => {
    setPeek(audit);
    setPeekTests([]);
    setPeekLoading(true);
    try {
      const res = await axiosInstance.get('/audit/admin/tests', {
        params: { auditId: audit.auditId, size: 500 },
      });
      setPeekTests(res.data?.auditProgressList || []);
    } catch {
      setPeekTests([]);
    } finally {
      setPeekLoading(false);
    }
  };

  const closePeek = () => { setPeek(null); setPeekTests([]); };

  // Unique frameworks for filter chips
  const frameworks = [...new Set(audits.map(a => a.frameworkName).filter(Boolean))];

  const filtered = audits.filter(a => {
    const matchFw = !fwFilter || a.frameworkName === fwFilter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (a.organization || '').toLowerCase().includes(q) ||
      (a.frameworkName || '').toLowerCase().includes(q) ||
      (a.performedBy || '').toLowerCase().includes(q) ||
      (a.auditId || '').toLowerCase().includes(q);
    return matchFw && matchSearch;
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const slice = filtered.slice(page * pageSize, (page + 1) * pageSize);

  // Stats
  const orgCount = new Set(audits.map(a => a.organization)).size;
  const fwCount  = new Set(audits.map(a => a.frameworkName)).size;

  // Peek stats
  const peekPassed  = peekTests.filter(t => t.lastAuditStatus === 'PASS').length;
  const peekFailed  = peekTests.filter(t => t.lastAuditStatus === 'FAIL').length;
  const peekPending = peekTests.filter(t => !t.lastAuditStatus).length;
  const peekTotal   = peekTests.length;
  const peekRate    = peekTotal > 0 ? (peekPassed / peekTotal) * 100 : 0;

  /* ── Sneak-peek panel ─────────────────────────────────────────────── */
  if (peek) {
    return (
      <div className="aa-page">
        <div className="aa-peek-topbar">
          <button className="aa-back-btn" onClick={closePeek}>
            <i className="fas fa-arrow-left" /> {t('AdminAudits.backToAllAudits')}
          </button>
          <div className="aa-peek-meta">
            <span className="aa-fw-chip">{peek.frameworkName || '—'}</span>
            <span className="aa-org-chip">{peek.organization || '—'}</span>
          </div>
        </div>

        <div className="aa-peek-header">
          <div className="aa-peek-title">{t('AdminAudits.auditSneakPeek')}</div>
          <div className="aa-peek-sub">
            {t('AdminAudits.auditLabel')} <span className="aa-mono">{peek.auditId}</span>
            {' · '}{t('AdminAudits.auditorLabel', { auditor: peek.performedBy || '—' })}
            {' · '}{t('AdminAudits.createdByLabel', { creator: peek.auditCreatedBy || '—' })}
            {' · '}{fmt(peek.startDate)}
          </div>
        </div>

        {peekLoading ? (
          <div className="aa-state-center">
            <i className="fas fa-circle-notch fa-spin aa-spin-icon" />
            <span>{t('AdminAudits.loadingTestResults')}</span>
          </div>
        ) : peekTests.length === 0 ? (
          <div className="aa-state-center">
            <i className="fas fa-clipboard aa-empty-icon-big" />
            <span>{t('AdminAudits.noTestResults')}</span>
          </div>
        ) : (
          <>
            {/* Progress bar */}
            <div className="aa-card">
              <div className="aa-card-header">
                {t('AdminAudits.complianceProgress')}
                <div className="aa-card-chips">
                  <span className="aa-chip-pass">{t('AdminAudits.passedCount', { count: peekPassed })}</span>
                  <span className="aa-chip-fail">{t('AdminAudits.failedCount', { count: peekFailed })}</span>
                  <span className="aa-chip-norun">{t('AdminAudits.pendingCount', { count: peekPending })}</span>
                </div>
              </div>
              <div className="aa-posture-bar-row">
                <div className="aa-posture-stat-rate" style={{ color: passRateColor(peekRate) }}>
                  {peekRate.toFixed(1)}%
                </div>
                <div className="aa-progress-track">
                  {peekPassed > 0 && (
                    <div className="aa-prog-seg aa-prog-pass" style={{ width: `${(peekPassed / peekTotal) * 100}%` }} />
                  )}
                  {peekFailed > 0 && (
                    <div className="aa-prog-seg aa-prog-fail" style={{ width: `${(peekFailed / peekTotal) * 100}%` }} />
                  )}
                  {peekPending > 0 && (
                    <div className="aa-prog-seg aa-prog-norun" style={{ width: `${(peekPending / peekTotal) * 100}%` }} />
                  )}
                </div>
                <div className="aa-progress-legend">
                  <span className="aa-leg-item"><span className="aa-leg-dot aa-leg-pass" />{t('AdminAudits.legendPass')}</span>
                  <span className="aa-leg-item"><span className="aa-leg-dot aa-leg-fail" />{t('AdminAudits.legendFail')}</span>
                  <span className="aa-leg-item"><span className="aa-leg-dot aa-leg-norun" />{t('AdminAudits.legendPending')}</span>
                </div>
              </div>
            </div>

            {/* Test table */}
            <div className="aa-card">
              <div className="aa-card-header">{t('AdminAudits.testResults')}</div>
              <div className="aa-table-wrap">
                <table className="aa-table">
                  <thead>
                    <tr>
                      <th>{t('AdminAudits.colTestName')}</th>
                      <th>{t('AdminAudits.colCategory')}</th>
                      <th>{t('AdminAudits.colControl')}</th>
                      <th>{t('AdminAudits.colType')}</th>
                      <th>{t('AdminAudits.colOwner')}</th>
                      <th>{t('AdminAudits.colStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {peekTests.map((t, i) => (
                      <tr key={i} className={t.lastAuditStatus === 'FAIL' ? 'aa-row-fail' : ''}>
                        <td className="aa-test-name">{t.testName}</td>
                        <td>{t.testCategory || '—'}</td>
                        <td className="aa-mono">{t.controlId || '—'}</td>
                        <td>
                          <span className={`aa-type-badge ${t.type === 'AUTOMATED' ? 'aa-type-auto' : 'aa-type-manual'}`}>
                            {t.type || 'MANUAL'}
                          </span>
                        </td>
                        <td>{t.testExecutioner || '—'}</td>
                        <td><StatusBadge status={t.lastAuditStatus} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Offer assistance CTA */}
            <div className="aa-assist-banner">
              <div className="aa-assist-text">
                <div className="aa-assist-title">
                  <i className="fas fa-hands-helping" /> {t('AdminAudits.offerHumanAssistance')}
                </div>
                <div className="aa-assist-sub">
                  {t('AdminAudits.assistSub', { count: peekFailed })}
                </div>
              </div>
              <a
                href={`mailto:?subject=Compliance%20Assistance%20for%20${encodeURIComponent(peek.organization || 'your organisation')}&body=Hi%2C%0A%0AWe%20noticed%20you%20have%20${peekFailed}%20failing%20compliance%20controls%20in%20your%20${encodeURIComponent(peek.frameworkName || '')}%20audit.%20We%20can%20help.%0A%0ARegards`}
                className="aa-assist-btn"
              >
                <i className="fas fa-envelope" /> {t('AdminAudits.contactOrganisation')}
              </a>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ── Main list view ───────────────────────────────────────────────── */
  return (
    <div className="aa-page">

      {/* Stats */}
      <div className="aa-stats">
        {[
          { label: t('AdminAudits.statTotalAudits'),   value: audits.length,  accent: '#2563eb' },
          { label: t('AdminAudits.statOrganisations'), value: orgCount,       accent: '#7c3aed' },
          { label: t('AdminAudits.statFrameworks'),    value: fwCount,        accent: '#0891b2' },
        ].map(s => (
          <div key={s.label} className="aa-stat-card" style={{ borderTopColor: s.accent }}>
            <div className="aa-stat-value" style={{ color: s.accent }}>{s.value}</div>
            <div className="aa-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="aa-toolbar">
        <div className="aa-search-wrap">
          <i className="fas fa-search aa-search-icon" />
          <input
            className="aa-search"
            placeholder={t('AdminAudits.searchPlaceholder')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
          {search && (
            <button className="aa-search-clear" onClick={() => setSearch('')}>
              <i className="fas fa-times" />
            </button>
          )}
        </div>

        <div className="aa-filter-group">
          <button
            className={`aa-filter-btn${fwFilter === '' ? ' aa-filter-btn-active' : ''}`}
            onClick={() => { setFwFilter(''); setPage(0); }}
          >{t('AdminAudits.filterAll')}</button>
          {frameworks.map(fw => (
            <button
              key={fw}
              className={`aa-filter-btn${fwFilter === fw ? ' aa-filter-btn-active' : ''}`}
              onClick={() => { setFwFilter(fw); setPage(0); }}
            >{fw}</button>
          ))}
        </div>

        <button className="aa-refresh-btn" onClick={load} disabled={loading} title={t('AdminAudits.refresh')}>
          <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="aa-error">
          <i className="fas fa-exclamation-circle" /> {error}
          <button className="aa-error-retry" onClick={load}>{t('AdminAudits.retry')}</button>
        </div>
      )}

      {/* Table */}
      <div className="aa-table-wrap">
        {loading ? (
          <div className="aa-state-center">
            <i className="fas fa-circle-notch fa-spin aa-spin-icon" />
            <span>{t('AdminAudits.loadingAudits')}</span>
          </div>
        ) : slice.length === 0 ? (
          <div className="aa-empty">
            <div className="aa-empty-icon"><i className="fas fa-clipboard-check" /></div>
            <div className="aa-empty-title">{t('AdminAudits.noAuditsFound')}</div>
            <div className="aa-empty-sub">
              {search || fwFilter
                ? t('AdminAudits.tryClearingFilters')
                : t('AdminAudits.auditsWillAppear')}
            </div>
          </div>
        ) : (
          <table className="aa-table">
            <thead>
              <tr>
                <th>{t('AdminAudits.colAuditId')}</th>
                <th>{t('AdminAudits.colOrganisation')}</th>
                <th>{t('AdminAudits.colFramework')}</th>
                <th>{t('AdminAudits.colAuditor')}</th>
                <th>{t('AdminAudits.colCreatedBy')}</th>
                <th>{t('AdminAudits.colDate')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {slice.map((audit, i) => (
                <tr key={i} className="aa-row-click" onClick={() => openPeek(audit)}>
                  <td className="aa-mono aa-audit-id">{audit.auditId || '—'}</td>
                  <td><span className="aa-org-chip">{audit.organization || '—'}</span></td>
                  <td>
                    {audit.frameworkName
                      ? <span className="aa-fw-chip">{audit.frameworkName}</span>
                      : <span className="aa-muted">—</span>}
                    {audit.auditType && (
                      <span className="aa-fw-chip" style={{ marginLeft: 4 }}>
                        {formatAuditType(audit.auditType, true)}
                      </span>
                    )}
                    {audit.tscSelection && (
                      <div className="aa-muted" style={{ fontSize: '0.7rem', fontFamily: 'ui-monospace, monospace' }}>
                        {audit.tscSelection}
                      </div>
                    )}
                  </td>
                  <td>{audit.performedBy || '—'}</td>
                  <td>{audit.auditCreatedBy || '—'}</td>
                  <td className="aa-date">{fmt(audit.startDate)}</td>
                  <td>
                    <button className="aa-peek-btn" onClick={e => { e.stopPropagation(); openPeek(audit); }}>
                      <i className="fas fa-eye" /> {t('AdminAudits.peek')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="aa-pagination">
          <button className="aa-page-btn" onClick={() => setPage(0)} disabled={page === 0}>
            <i className="fas fa-angle-double-left" />
          </button>
          <button className="aa-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
            <i className="fas fa-angle-left" />
          </button>
          <span className="aa-page-info">{t('AdminAudits.pageInfo', { current: page + 1, total: totalPages, count: filtered.length })}</span>
          <button className="aa-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
            <i className="fas fa-angle-right" />
          </button>
          <button className="aa-page-btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>
            <i className="fas fa-angle-double-right" />
          </button>
        </div>
      )}
    </div>
  );
}
